// src/pages/PathPage.tsx
// Ad landing page at /path — the MLmate + Capstone "Learning Path" campaign
// (enhancement&promotions/Cubemate-India-Meta-Jul2026-MLmate-Capstone.md).
// The page IS the ad's promise: SEE it (MLmate) → LEARN it (Bookmate) →
// BUILD it (Capstone, Pro). Light cream theme matching the HomePage palette
// (switched from dark 2026-07-09); code blocks stay dark like HomePage's.
// Self-contained: scoped to .path-root. CTA → /login (paywall guard routes on).
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STAGES = ['Dataset', 'Preprocess', 'Features', 'Model', 'Train', 'Predict'];

const PathPage: React.FC = () => {
  const navigate = useNavigate();
  const [running, setRunning] = useState<'idle' | 'running' | 'done'>('idle');
  const [runtime, setRuntime] = useState<number | null>(null);
  const runStartRef = useRef<number>(0);

  // Track UTM-tagged ad visits (same pattern as StartPage.tsx / v3.19.7.3).
  // Row lands in ad_visits; visit_id stashed so signup links it to the user.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (!hasUtm) return;
    const apiUrl = process.env.REACT_APP_API_URL || '';
    fetch(`${apiUrl}/api/ad-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        landing_page: '/path',
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_content: params.get('utm_content') || undefined,
        utm_term: params.get('utm_term') || undefined,
        gclid: params.get('gclid') || undefined,
        referrer: document.referrer || undefined,
      }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.visit_id) {
          localStorage.setItem('ad_visit_id', String(data.visit_id));
        }
      })
      .catch(() => { /* tracking is best-effort; never block the landing page */ });
  }, []);

  const goStart = () => {
    // Meta Pixel conversion signal (same as LandingPage.tsx CTA)
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: 'path_landing_cta' });
    }
    navigate('/login');
  };

  const runDemo = () => {
    if (running !== 'idle') return;
    setRunning('running');
    runStartRef.current = performance.now();
    setTimeout(() => {
      setRuntime(Math.round(performance.now() - runStartRef.current));
      setRunning('done');
      setTimeout(() => setRunning('idle'), 1600);
    }, 800);
  };

  return (
    <div className="path-root">
      <style>{`
        .path-root {
          --bg: #fefae8;
          --bg-2: #ffffff;
          --bg-3: #fff5d1;
          --line: #1a1a2e15;
          --ink: #1a1a2e;
          --ink-soft: #40405a;
          --ink-mute: #6b6b85;
          --teal: #0d9488;
          --violet: #7c3aed;
          --p-gradient: linear-gradient(135deg, #14b8a6 0%, #8b5cf6 100%);
          font-family: 'Inter', system-ui, sans-serif;
          background: var(--bg);
          color: var(--ink);
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
          color-scheme: light;
        }
        .path-root .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .path-root .p-grad-text {
          background: var(--p-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .path-root .p-grad-bg { background: var(--p-gradient); }
        .path-root .p-card {
          background: var(--bg-2);
          border: 1.5px solid var(--line);
          border-radius: 18px;
          box-shadow: 0 1px 0 rgba(26,26,46,.04), 0 8px 24px -10px rgba(26,26,46,.08);
        }
        .path-root .p-chip {
          border: 1px solid #0d948855;
          background: #0d94880f;
          color: var(--teal);
        }
        .path-root .p-code {
          background: #1a1525;
          border: 1px solid #1a152500;
          color: #e8e3f0;
          border-radius: 12px;
          padding: 14px 16px;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 12px;
          line-height: 1.7;
          white-space: pre;
          box-shadow: 0 8px 32px -8px rgba(26,21,37,.4);
        }
        /* MLmate pipeline: stages light up in sequence, forever */
        @keyframes p-stage {
          0%, 100% { border-color: #1a1a2e15; color: #8a8aa3; background: #ffffff; }
          8%, 16% { border-color: #14b8a6; color: #0d9488; background: #14b8a614; }
        }
        .path-root .p-stage { animation: p-stage 7.2s linear infinite; }
        @keyframes p-result-pop {
          0%, 78% { opacity: 0; transform: scale(0.92); }
          86%, 96% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.92); }
        }
        .path-root .p-result { animation: p-result-pop 7.2s linear infinite; }
        @keyframes p-spin { to { transform: rotate(360deg); } }
        .path-root .p-spin { animation: p-spin 1s linear infinite; display: inline-block; }
        @keyframes p-pulse-edge { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
        .path-root .p-edge { animation: p-pulse-edge 1.6s ease-in-out infinite; }
        .path-root .p-num {
          background: var(--p-gradient);
          color: white;
          font-weight: 800;
          box-shadow: 0 8px 24px -8px #14b8a680;
        }
      `}</style>

      {/* ───── NAV (logo + single CTA) ───── */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b" style={{ background: 'rgba(254,250,232,0.85)', borderColor: '#1a1a2e14' }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/cubemate-logo.png" alt="Cubemate" className="w-8 h-8 rounded-lg" />
            <span className="font-bold tracking-tight text-lg">Cubemate</span>
          </a>
          <button onClick={goStart} className="px-4 py-2 text-sm font-semibold rounded-full p-grad-bg text-white hover:opacity-90 transition shadow-md">
            Start free →
          </button>
        </div>
      </nav>

      {/* ───── HERO ───── */}
      <header className="pt-32 pb-14 px-6 text-center">
        <p className="text-xs mono uppercase tracking-widest font-semibold mb-4" style={{ color: 'var(--teal)' }}>your path into AI &amp; coding</p>
        <h1 className="text-5xl md:text-6xl font-black tracking-tight leading-[1.05] mb-6">
          See it. Learn it. <span className="p-grad-text">Build it.</span>
        </h1>
        <p className="text-xl max-w-2xl mx-auto leading-relaxed mb-8" style={{ color: 'var(--ink-soft)' }}>
          Watch a real machine learning model train — live. Learn with tutorials where every line runs.
          Then build a real project you can show in an interview. One path, one place.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <button onClick={goStart} className="px-8 py-3.5 font-semibold rounded-full p-grad-bg text-white hover:opacity-90 transition shadow-lg">
            Start free →
          </button>
          <a href="#see" className="px-8 py-3.5 font-semibold rounded-full border-2 bg-white transition hover:border-[#0d948866]" style={{ borderColor: '#1a1a2e1a', color: 'var(--ink-soft)' }}>
            See the path ↓
          </a>
        </div>
        <p className="mt-5 text-base" style={{ color: 'var(--ink-mute)' }}>7-day free trial · no card · Pro ₹249/mo when you're ready to build</p>
      </header>

      {/* ───── STEP 1 — SEE IT (MLmate) ───── */}
      <section id="see" className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="p-num w-14 h-14 rounded-2xl grid place-items-center text-xl mb-5">1</div>
            <p className="text-xs mono uppercase tracking-widest font-semibold mb-2" style={{ color: 'var(--teal)' }}>MLmate · included in free trial</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Watch machine learning <span className="p-grad-text">actually happen.</span></h2>
            <p className="text-lg leading-relaxed mb-4" style={{ color: 'var(--ink-soft)' }}>
              MLmate is a map of 10 real problems — fraud detection, churn, house prices. Click one, press Run,
              and the full pipeline lights up stage by stage: real rows, a real model training, real accuracy, a real prediction.
            </p>
            <ul className="space-y-2 text-base" style={{ color: 'var(--ink-soft)' }}>
              <li>✓ No install, no dataset hunting, no setup</li>
              <li>✓ 13 algorithms — XGBoost, Random Forest, Isolation Forest &amp; more</li>
              <li>✓ Every stage explained in plain language: <em>why</em> this step exists</li>
            </ul>
          </div>

          {/* MLmate mockup: problem node → pipeline stages animating */}
          <div className="p-card p-6">
            <div className="flex items-center justify-between mb-5">
              <span className="text-xs mono" style={{ color: 'var(--ink-mute)' }}>MLmate — Fraud Detection</span>
              <span className="p-chip text-[11px] font-bold px-2.5 py-1 rounded-full">● live run</span>
            </div>
            <div className="flex items-center gap-3 mb-5">
              <div className="rounded-xl px-4 py-3 border" style={{ borderColor: '#14b8a655', background: '#14b8a610' }}>
                <div className="text-[11px]" style={{ color: 'var(--ink-mute)' }}>💳 Problem</div>
                <div className="font-bold text-sm">Which payment is a thief?</div>
              </div>
              <div className="p-edge text-xl" style={{ color: 'var(--teal)' }}>→</div>
              <div className="rounded-xl px-4 py-3 border" style={{ borderColor: '#8b5cf655', background: '#8b5cf610' }}>
                <div className="text-[11px]" style={{ color: 'var(--ink-mute)' }}>Model</div>
                <div className="font-bold text-sm">Isolation Forest</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {STAGES.map((s, i) => (
                <div
                  key={s}
                  className="p-stage rounded-lg border px-2 py-2 text-center text-[11px] font-semibold mono"
                  style={{ animationDelay: `${i * 1.0}s` }}
                >
                  {s}
                </div>
              ))}
            </div>
            <div className="p-result mt-5 rounded-xl border px-4 py-3 flex items-center justify-between" style={{ borderColor: '#14b8a655', background: '#14b8a60d' }}>
              <div>
                <div className="text-[11px] mono" style={{ color: 'var(--ink-mute)' }}>PREDICTION</div>
                <div className="font-black text-lg" style={{ color: 'var(--teal)' }}>Fraud — 92% probability</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] mono" style={{ color: 'var(--ink-mute)' }}>20,000 rows</div>
                <div className="text-sm font-bold mono" style={{ color: 'var(--ink-soft)' }}>Recall 0.49</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── STEP 2 — LEARN IT (Bookmate, interactive) ───── */}
      <section className="py-16 px-6" style={{ background: 'linear-gradient(180deg, transparent, #fff5d180, transparent)' }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* Runnable demo card (order first on desktop so layout zig-zags) */}
          <div className="p-card p-6 order-2 lg:order-1">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs mono" style={{ color: 'var(--ink-mute)' }}>Bookmate — Chapter 3 · Predicting a price</span>
            </div>
            <pre className="p-code overflow-x-auto">{`model = XGBRegressor(n_estimators=500)
model.fit(X_train, y_train)

house = {"living_area": 2400, "quality": 8}
print(predict(model, house))`}</pre>
            <div
              className="overflow-hidden transition-all duration-500"
              style={{ opacity: running === 'idle' ? 0 : 1, maxHeight: running === 'idle' ? 0 : 140 }}
            >
              <div className="mt-3 border-l-4 pl-3 py-1" style={{ borderColor: '#14b8a6' }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] mono uppercase tracking-wider font-bold" style={{ color: 'var(--teal)' }}>output</span>
                  {runtime !== null && <span className="text-[10px] mono" style={{ color: 'var(--ink-mute)' }}>· {runtime} ms</span>}
                </div>
                <pre className="mono text-sm rounded p-2" style={{ background: '#d1fae566', color: '#047857' }}>{`$438,042  (R² 0.86 on held-out test)`}</pre>
              </div>
            </div>
            <div className="flex gap-2 pt-3 items-center">
              <button
                onClick={runDemo}
                disabled={running !== 'idle'}
                className="px-4 py-2 text-xs font-semibold rounded-full text-white transition flex items-center gap-1.5 shadow-md disabled:opacity-70"
                style={{ background: '#0d9488' }}
              >
                <span className={running === 'running' ? 'p-spin' : ''}>
                  {running === 'idle' ? '▶' : running === 'running' ? '◐' : '✓'}
                </span>
                <span>{running === 'idle' ? 'Run this code' : running === 'running' ? 'Running…' : 'Done'}</span>
              </button>
              {running === 'idle' && <span className="text-xs" style={{ color: 'var(--ink-mute)' }}>← it really executes</span>}
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="p-num w-14 h-14 rounded-2xl grid place-items-center text-xl mb-5">2</div>
            <p className="text-xs mono uppercase tracking-widest font-semibold mb-2" style={{ color: 'var(--teal)' }}>Bookmate + Chat · included in free trial</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Learn with tutorials where <span className="p-grad-text">every line runs.</span></h2>
            <p className="text-lg leading-relaxed mb-4" style={{ color: 'var(--ink-soft)' }}>
              Ask for any topic — Python, ML basics, Java OOP — and Cubemate writes you a book-style tutorial,
              8–12 chapters that read like a real book. The difference: every code example is live.
              Run it, change it, run it again — right inside the page.
            </p>
            <ul className="space-y-2 text-base" style={{ color: 'var(--ink-soft)' }}>
              <li>✓ No videos to sit through, no IDE to install</li>
              <li>✓ Download finished tutorials as PDF — build your own library</li>
              <li>✓ Chat for quick questions; it writes <em>and runs</em> the code</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ───── STEP 3 — BUILD IT (Capstone) ───── */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="p-num w-14 h-14 rounded-2xl grid place-items-center text-xl mb-5">3</div>
            <p className="text-xs mono uppercase tracking-widest font-semibold mb-2" style={{ color: 'var(--violet)' }}>Capstone · Pro</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Finish with a project you can <span className="p-grad-text">show in an interview.</span></h2>
            <p className="text-lg leading-relaxed mb-4" style={{ color: 'var(--ink-soft)' }}>
              Describe what you want to build, in your own words — a snake game, a stock tracker, a job scraper.
              Capstone plans the project, then builds it <em>with</em> you, step by step, in a real cloud workspace.
              You make real decisions. You finish with something that's genuinely yours.
            </p>
            <ul className="space-y-2 text-base" style={{ color: 'var(--ink-soft)' }}>
              <li>✓ Real files, real terminal, real running app — playable in your browser</li>
              <li>✓ Python, C/C++ and browser games (WebAssembly)</li>
              <li>✓ Your portfolio answer to "show me something you built"</li>
            </ul>
          </div>

          {/* Capstone mockup: prompt → plan → built */}
          <div className="p-card p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs mono" style={{ color: 'var(--ink-mute)' }}>Capstone — new project</span>
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full border" style={{ borderColor: '#8b5cf655', background: '#8b5cf614', color: 'var(--violet)' }}>Pro</span>
            </div>
            <div className="rounded-xl px-4 py-3 mb-3 text-sm" style={{ background: 'var(--bg-3)' }}>
              <span className="text-[11px] mono block mb-1" style={{ color: 'var(--ink-mute)' }}>you</span>
              A snake game I can play in the browser
            </div>
            <div className="rounded-xl px-4 py-3 mb-3" style={{ background: '#8b5cf610', border: '1px solid #8b5cf633' }}>
              <span className="text-[11px] mono block mb-2" style={{ color: 'var(--ink-mute)' }}>capstone · plan</span>
              <ul className="space-y-1.5 text-sm" style={{ color: 'var(--ink-soft)' }}>
                <li>✓ Game loop, keyboard controls, scoring</li>
                <li>✓ Collision, pause/restart, polished playfield</li>
                <li>✓ Compile to game.html — playable in browser</li>
              </ul>
            </div>
            <div className="rounded-xl px-4 py-3 flex items-center justify-between" style={{ background: '#14b8a60d', border: '1px solid #14b8a644' }}>
              <div className="text-sm font-bold" style={{ color: 'var(--teal)' }}>▶ game.html built — press Play</div>
              <div className="text-[11px] mono" style={{ color: 'var(--ink-mute)' }}>5 files · 6.3s build</div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── PRICING (two columns only — trial vs Pro) ───── */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs mono uppercase tracking-widest font-semibold mb-2" style={{ color: 'var(--teal)' }}>simple pricing</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">Start free. Go Pro to build.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-card p-8">
              <h3 className="font-black text-xl mb-1">Free trial</h3>
              <p className="text-3xl font-black mb-4">₹0 <span className="text-sm font-medium" style={{ color: 'var(--ink-mute)' }}>/ 7 days · no card</span></p>
              <ul className="space-y-2.5 text-base mb-6" style={{ color: 'var(--ink-soft)' }}>
                <li>✓ <strong style={{ color: 'var(--ink)' }}>MLmate</strong> — watch real models train</li>
                <li>✓ <strong style={{ color: 'var(--ink)' }}>Bookmate</strong> — runnable tutorials</li>
                <li>✓ <strong style={{ color: 'var(--ink)' }}>Chat</strong> — code written &amp; executed for you</li>
                <li>✓ 125 credits included</li>
              </ul>
              <button onClick={goStart} className="w-full py-3 font-semibold rounded-full border transition hover:border-[#0d9488]" style={{ borderColor: '#0d948855', color: 'var(--teal)' }}>
                Start free →
              </button>
            </div>
            <div className="p-card p-8 relative" style={{ borderColor: '#8b5cf666' }}>
              <span className="absolute -top-3 left-8 text-[11px] font-bold px-3 py-1 rounded-full p-grad-bg text-white">THE FULL PATH</span>
              <h3 className="font-black text-xl mb-1">Pro</h3>
              <p className="text-3xl font-black mb-4">₹249 <span className="text-sm font-medium" style={{ color: 'var(--ink-mute)' }}>/ month</span></p>
              <ul className="space-y-2.5 text-base mb-6" style={{ color: 'var(--ink-soft)' }}>
                <li>✓ Everything in the trial</li>
                <li>✓ <strong style={{ color: 'var(--ink)' }}>Capstone</strong> — build your portfolio project</li>
                <li>✓ 400 credits / month + Workflow + PDF sharing</li>
                <li>✓ Cancel anytime</li>
              </ul>
              <button onClick={goStart} className="w-full py-3 font-semibold rounded-full p-grad-bg text-white hover:opacity-90 transition">
                Start free, upgrade when ready →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {[
            ['Is the code really running?', 'Yes. MLmate trains and runs real models on our servers with real metrics. Tutorial code executes in a cloud sandbox. Capstone projects run in their own cloud workspace with real files and a real terminal. Nothing is a video or a mock-up.'],
            ['Do I need to install anything?', 'No. Everything — the ML runs, the tutorials, your Capstone project — happens in the browser. Any laptop works.'],
            ['What happens after 7 days?', 'The free trial gives you 125 credits for 7 days, no card needed. If you love it, Pro is ₹249/month and unlocks Capstone. If not, you lose nothing.'],
          ].map(([q, a]) => (
            <details key={q} className="p-card px-6 py-4 group">
              <summary className="font-bold cursor-pointer list-none flex justify-between items-center">
                {q}
                <span className="transition group-open:rotate-45 text-lg" style={{ color: 'var(--teal)' }}>+</span>
              </summary>
              <p className="mt-3 text-base leading-relaxed" style={{ color: 'var(--ink-soft)' }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto rounded-3xl p-12 md:p-16 text-center" style={{ background: 'linear-gradient(135deg, #14b8a61f, #8b5cf61f)', border: '1px solid #14b8a633' }}>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
            See it today. <span className="p-grad-text">Build it this month.</span>
          </h2>
          <p className="text-xl mb-8 max-w-xl mx-auto" style={{ color: 'var(--ink-soft)' }}>
            Your first model trains in the next five minutes. Free for 7 days, no card needed.
          </p>
          <button onClick={goStart} className="px-9 py-4 font-bold rounded-full p-grad-bg text-white hover:opacity-90 transition shadow-xl">
            Start free →
          </button>
        </div>
      </section>

      {/* ───── FOOTER ───── */}
      <footer className="border-t py-10 px-6" style={{ borderColor: '#1a1a2e14' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm" style={{ color: 'var(--ink-mute)' }}>
          <div className="flex items-center gap-2.5">
            <img src="/cubemate-logo.png" alt="Cubemate" className="w-7 h-7 rounded-lg" />
            <span className="font-semibold" style={{ color: 'var(--ink)' }}>Cubemate</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/privacy" className="hover:text-black transition">Privacy</a>
            <a href="/terms" className="hover:text-black transition">Terms</a>
          </div>
        </div>
        <p className="text-center text-xs mt-6" style={{ color: 'var(--ink-mute)' }}>© 2026 Cubemate. Made in India.</p>
      </footer>
    </div>
  );
};

export default PathPage;
